import React from "react";
import styled from "styled-components";
import { Subtitle } from "./shared/Subtitle";
import { Title } from "./shared/Title";
import { FAQItem } from "./FAQItem";
import SchoolIcon from "@mui/icons-material/School";
import Grid from "@mui/material/Grid2";
import { ScrollableImage } from "./shared/ScrollableImage";
import { theme } from "../theme";

const Bg = styled.div`
  background-color: ${theme.colors.white};
  width: 100vw;
`;

const WrapperStyled = styled.div`
  margin: 0 auto;
  max-width: 1000px;
  padding: 70px 5%;

  & p {
    max-width: 750px;
    margin: auto;
  }
`;

const GridStyled = styled(Grid)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  max-width: 280px;
  margin: auto;

  @media (min-width: 740px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`;

const BenefitsStyled = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;

  & svg {
    color: ${theme.colors.blue};
    margin-right: 5px
  }`
);

export const Curricula = () => {
  return (
    <Bg>
      <WrapperStyled>
        <Subtitle>Lo que vas a aprender</Subtitle>
        <Title>
          Las tecnologías que te convertirán en un desarrollador imprescindible
        </Title>
        <p>
          Domina las tecnologías de desarrollo web más demandadas y conviértete
          en el candidato que todas las empresas buscan. Al finalizar,{" "}
          <b>estarás listo para superar entrevistas técnicas</b> y destacar en
          el mercado laboral.
        </p>
        <ScrollableImage src="/img/subjects.svg" />
        <FAQItem question="Módulo I: Maquetación Web (HTML, CSS, SASS, Bootstrap y Tailwind)">
          En este modulo aprenderás a transformar diseños visuales en páginas
          web funcionales utilizando <b>HTML y CSS</b>.
          <br />
          <br />
          Te enseñaremos a estructurar correctamente el contenido,{" "}
          <b>aplicar estilos</b>, adaptar la maquetación a diversas pantallas (
          <b>responsive</b>) y seguir buenas prácticas de <b>accesibilidad</b> y
          semántica.
          <br />
          <br />
          Es una <b>base fundamental</b> para cualquier desarrollador web y el
          primer paso para construir sitios modernos y profesionales.
        </FAQItem>
        <FAQItem question="Transversal I: Control de versiones (Git y GitHub)">
          En el desarrollo de software, cada cambio cuenta. ¿Cómo gestionar
          modificaciones, colaborar en equipo o revertir errores sin perder el
          control?
          <br />
          <br />
          La respuesta está en <b>Git</b>, el sistema de control de versiones
          más usado, y <b>GitHub</b>, la plataforma que potencia tu trabajo
          colaborativo.
          <br />
          <br />
          Con <b>Git</b> guardas un historial completo de tu proyecto,
          experimentas sin miedo y trabajas en equipo de forma ordenada.
          <br />
          <br />
          <b>GitHub</b> te permite compartir código, contribuir a proyectos open
          source y mostrar tu trabajo al mundo.
        </FAQItem>
        <FAQItem question="Módulo II: Javascript">
          En este modulo aprenderás a programar con <b>JavaScript</b>, el
          lenguaje que permite crear <b>páginas web dinámicas e interactivas</b>
          .
          <br />
          <br />
          Comenzaremos con los <b>fundamentos</b> (variables, funciones,
          condicionales, bucles), y avanzaremos hacia el manejo del <b>DOM</b>,
          eventos, validación de formularios y más.
          <br />
          <br />
          También exploraremos conceptos modernos como <b>ES6+</b> (arrow
          functions, destructuring, let, const), uso de <b>fetch</b> para
          consumir APIs, y la gestión de <b>peticiones asíncronas</b> (promesas
          y async/await).
          <br />
          <br />
          Es un módulo clave para empezar a crear sitios dinámicos y
          aplicaciones web interactivas.
        </FAQItem>
        <FAQItem question="Módulo III: React y Redux">
          En este modulo aprenderás a construir aplicaciones web modernas
          utilizando <b>React</b>, la biblioteca más popular para crear{" "}
          <b>interfaces</b> de usuario <b>dinámicas y reutilizables</b>.
          <br />
          <br />
          Empezaremos con los fundamentos: componentes, props, estado, hooks
          (como useState y useEffect), y el manejo del ciclo de vida.
          <br />
          <br />
          Luego, profundizaremos en <b>Redux</b>, la herramienta más utilizada
          para gestionar el estado global de una aplicación de manera predecible
          y escalable. Aprenderás a conectar Redux con React usando react-redux,
          crear acciones y reducers.
          <br />
          <br />
          Al finalizar, tendrás la capacidad de desarrollar{" "}
          <b>aplicaciones robustas</b>, escalables y fáciles de mantener.
        </FAQItem>
        <FAQItem question="Transversal II: Introducción a Backend y bases de datos (Firebase)">
          En este módulo aprenderás los conceptos fundamentales sobre el
          desarrollo <b>backend</b>, el motor que hace funcionar una aplicación
          web desde el servidor.
          <br />
          <br />
          Comenzarás entendiendo cómo funciona una <b>API REST</b>, cómo manejar{" "}
          <b>peticiones HTTP</b> (GET, POST, PUT, DELETE). Verás cómo enviar y
          recibir datos en formato JSON y cómo manejar códigos de estado HTTP
          (como 200, 404, 500...).
          <br />
          <br />
          Además, aprenderás las ventajas y flexibilidad que aporta{" "}
          <b>Firebase</b> y su herramienta de backend como servicio (BaaS).
        </FAQItem>

        <FAQItem question="Transversal III: UX UI (Figma)">
          Aunque el propósito del bootcamp no es convertirte en diseñador, sí te
          vamos a dar nociones de <b>UX UI</b> muy útiles para entenderte con
          estos perfiles en tu desempeño laboral.
          <br />
          <br />
          Con <b>Figma</b> aprenderás a <b>diseñar pantallas</b>, botones, menús
          y más. Es una herramienta muy fácil de usar que te permitirá crear
          prototipos visuales de tus aplicaciones. <br />
        </FAQItem>
        <FAQItem question="Transversal IV: Metodologías ágiles (Scrum, Kanban)">
          Hacer una web no es solo programar: también hay que <b>organizarse</b>
          . Durante estas clases conocerás <b>Scrum</b>, una forma divertida y
          ordenada de <b>trabajar en grupo</b>.
          <br />
          <br />
          Aprenderás qué es un sprint, cómo <b>dividir tareas</b>, y cómo se{" "}
          <b>trabaja en equipos</b> reales de empresas de tecnología.
        </FAQItem>
        <FAQItem question="Módulo IV: Proyecto final">
          En el módulo final, tú y tus compañeros trabajaréis juntos en un
          proyecto final que <b>simula un entorno de desarrollo real</b>.
          <br />
          <br />
          Durante este proyecto, tendrás la oportunidad de aplicar todo lo
          aprendido a lo largo del curso: desde la maquetación web, pasando por
          el uso de JavaScript y React, hasta la gestión de estados con Redux y
          la implementación de APIs.
          <br />
          <br />
          Los profesores estarán allí como guías para resolver dudas, dar{" "}
          <b>feedback</b> y asegurar que el proyecto avance sin problemas.
          <br />
          <br />
          Además, tendrás que <b>colaborar con tus compañeros</b>, asignar
          tareas, gestionar tiempos y aplicar las metodologías ágiles, como
          Scrum, para simular un flujo de trabajo profesional.
          <br />
          <br />
          Al final, tendrás un proyecto funcional que podrás presentar como
          <b> parte de tu portafolio</b>, demostrando tus habilidades en un
          entorno real de desarrollo.
        </FAQItem>
        <FAQItem question="Módulo V: Preparación para entrevistas">
          En estas clases te ayudamos a prepararte para las{" "}
          <b>entrevistas de trabajo</b> en el mundo del desarrollo.
          <br />
          <br />
          Los <i>profes</i> ya hemos pasado y hemos hecho varias entrevistas,
          así que conocemos las preguntas más comunes y lo que los
          entrevistadores esperan de un desarrollador principiante.
          <br />
          <br />
          Te prepararemos para preguntas específicas de tecnologías como
          JavaScript, React y Git. También te daremos <b>consejos</b> para
          aplicar un <b>pensamiento crítico</b> y cómo resolver problemas en
          tiempo real.
        </FAQItem>

        <Grid
          container
          spacing={3}
          style={{ margin: "50px auto 0" }}
          id="methodology"
        >
          <GridStyled item size={{ xs: 12, sm: 4 }}>
            <BenefitsStyled>
              <SchoolIcon /> Los lenguajes más demandados
            </BenefitsStyled>
          </GridStyled>
          <GridStyled item size={{ xs: 12, sm: 4 }}>
            <BenefitsStyled>
              <SchoolIcon /> Temarios actualizados
            </BenefitsStyled>
          </GridStyled>
          <GridStyled item size={{ xs: 12, sm: 4 }}>
            <BenefitsStyled>
              <SchoolIcon /> Ejercicios 100% prácticos
            </BenefitsStyled>
          </GridStyled>
        </Grid>
      </WrapperStyled>
    </Bg>
  );
};
